import { useEffect } from 'react';
import { dehydrate, type DehydratedState, QueryClient } from '@tanstack/react-query';
import { getLocaleCodeByCountryCode, SUPPORTED_LOCALES_OBJ, type SupportedLocale } from '@zep-us/i18n';
import { type GetServerSidePropsContext, type GetServerSidePropsResult, type NextPageWithLayout } from 'next';
import { useRouter } from 'next/router';
import { type QueryDataType } from '@/api/api.type';
import { getGetQuizUserInfoQueryKey, getQuizUserInfo, type searchUserPublicQuizsets } from '@/api/gen/quiz-user/quiz-user.gen';
import { getProductSidebarLayout } from '@/layout/ProductSidebarLayout';
import { getDefaultQuizsetSearchReq, useFetchUserQuizes } from '@/module/profile/user/hooks/useFetchUserQuizes';
import { getUserProfileSeoContent } from '@/module/profile/user/seo';
import { useIsInExternalPlatform } from '@/shared/externalPlatform';
import { useUserFoldersQuery } from '@/shared/features/quiz-folder';
import { serverLogger } from '@/shared/logger/serverLogger';
import { withSsrLogging } from '@/shared/logger/withSsrLogging';
import { getDefaultServerSideProps } from '@/utils/next';
import { setPublicPageCache } from '@/utils/ssr';
import { readListSearchQuery } from './utils/readListSearchQuery';
import { buildUserFolderVariables, USER_FOLDER_PREVIEW_LIMIT } from './utils/userFolderVariables';
import { UserProfilePageView } from './UserProfilePageView';
type UserProfilePageProps = {
  userHashId: string;
  seoContent: object;
  canonicalUrl: string;
  dehydratedState: DehydratedState;
};
const UserProfilePageComponent: NextPageWithLayout<UserProfilePageProps> = ({
  userHashId
}) => {
  const isInExternalPlatform = useIsInExternalPlatform();
  const router = useRouter();
  useEffect(() => {
    if (isInExternalPlatform) {
      router.back();
    }
  }, [isInExternalPlatform, router]);
  if (isInExternalPlatform) return null;
  return <UserProfilePageView userHashId={userHashId} data-sentry-element="UserProfilePageView" data-sentry-component="UserProfilePageComponent" data-sentry-source-file="index.page.tsx" />;
};
UserProfilePageComponent.getLayout = function (page) {
  return getProductSidebarLayout(page, 'p-0', {
    hideMobileBottomNavigation: true,
    hideContentPaddingWithoutSidebar: true
  });
};
export const getServerSideProps = withSsrLogging('user-profile', async (context: GetServerSidePropsContext): Promise<GetServerSidePropsResult<UserProfilePageProps>> => {
  const {
    params
  } = context;
  const queryClient = new QueryClient();
  const userHashId = params?.userHashId as string ?? null;
  if (!userHashId) {
    return {
      notFound: true
    };
  }
  const prefetchUser = queryClient.prefetchQuery({
    queryKey: getGetQuizUserInfoQueryKey(userHashId),
    queryFn: () => getQuizUserInfo(userHashId)
  });

  // 클라(`useSearchQuizQuery`)와 같은 쿼리 이름(`q`·`order`·`period`)을 읽는다 — 다르면 캐시 키가 갈라져
  // SSR 결과를 버리고 다시 받는다.
  const {
    searchText,
    order,
    period
  } = readListSearchQuery(context.query);
  const quizVariables = {
    userId: userHashId,
    quizsetSearchReq: getDefaultQuizsetSearchReq({
      searchText,
      order,
      period
    })
  } as const;
  const prefetchUserQuizSet = queryClient.prefetchInfiniteQuery(useFetchUserQuizes.getFetchOptions(quizVariables));

  // 공개 폴더 섹션. 실패해도 throw 하지 않는다(prefetch) — 프로필은 그대로 뜨고 섹션만 빠진다.
  const prefetchUserFolders = queryClient.prefetchInfiniteQuery(useUserFoldersQuery.getFetchOptions(buildUserFolderVariables({
    userId: userHashId,
    searchText,
    order,
    period,
    limit: USER_FOLDER_PREVIEW_LIMIT
  })));
  try {
    const [{
      props: defaultProps
    }] = await Promise.all([getDefaultServerSideProps({
      ...context,
      namespaces: ['common', 'country']
    }), prefetchUser, prefetchUserQuizSet, prefetchUserFolders]);
    const userRes = queryClient.getQueryData<QueryDataType<typeof getQuizUserInfo>>(getGetQuizUserInfoQueryKey(userHashId));
    const quizsetsRes = queryClient.getQueryData<{
      pages: QueryDataType<typeof searchUserPublicQuizsets>[];
    }>(useFetchUserQuizes.getKey(quizVariables));
    if (!userRes || !userRes.data || !userRes.data.countryCode || !userRes.data.userType) {
      return {
        notFound: true
      };
    }
    // 캐시 헤더는 성공 데이터 검증 뒤에만 — 위 notFound/아래 catch 경로가
    // cacheable 헤더를 달고 나가면 오류 응답이 공유 캐시에 남는다 (#3746 P1).
    setPublicPageCache(context, {
      cacheable: true,
      profile: 'user'
    });
    // TODO: QuizUserInfoRes 에 updatedAt 이 노출되면 handleIfModifiedSince 적용

    const quizSets = (quizsetsRes?.pages ?? []).length > 0 ? (quizsetsRes?.pages ?? []).flatMap(page => page.data ?? []) : [];
    const mappedLocale = getLocaleCodeByCountryCode(userRes.data.countryCode);
    const canonicalUrl = `${process.env.NEXT_PUBLIC_BASE_URL}${mappedLocale === SUPPORTED_LOCALES_OBJ.ko ? '' : `/${mappedLocale}`}/profile/${userHashId}`;
    const seoContent = getUserProfileSeoContent({
      user: {
        ...userRes.data,
        userHashId
      },
      quizSets: quizSets,
      _nextI18Next: defaultProps._nextI18Next,
      locale: context.locale as SupportedLocale,
      canonicalUrl
    });
    return {
      props: {
        seoContent,
        canonicalUrl,
        userHashId,
        ...defaultProps,
        dehydratedState: dehydrate(queryClient)
      }
    };
  } catch (e) {
    serverLogger.error({
      err: e
    }, 'user-profile ssr error');
    return {
      redirect: {
        destination: '/500',
        permanent: false
      }
    };
  }
});
export default UserProfilePageComponent;